import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, styled, Typography } from '@mui/material';
import { toast } from '@side/pantry';
import { useCallback, useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import { AllowedImageFileExtensions } from 'src/types';
import { getAcceptedDropzoneFiles } from './helpers/dropZoneAccept';
import { resizeImage } from './helpers/getCroppedImage';
import { ImagePreview } from './ImagePreview';
import { initialValidationState } from './initialState';
import { UploadImageModalContext } from './UploadImageModalContext';

export const maxPayloadMegabytes = 7;
const maxFileSizeBytes = maxPayloadMegabytes * 1024 * 1024;
export const invalidFileSizeErrorMsg = `Upload failed. The file size must be less than ${maxPayloadMegabytes} MB.`;
export const invalidImageErrorMsg = 'Please upload an image file';

export type UploadFormContentProps = {
  uploading?: boolean;
  selectedFile: File | null;
  fileValidation: typeof initialValidationState;
  setSelectedFile: (file: File | null) => void;
  setFileValidation: (fileValidation: typeof initialValidationState) => void;
};

const ImageBackground = styled(Box)(({ theme }) => ({
  border: `1.5px dashed ${theme.palette.other.outlineBorder}`,
  width: '100%',
  position: 'relative',
  borderRadius: '4px',
  display: 'flex',
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  '&:hover': { backgroundColor: theme.palette.action.hover },
  '&[aria-disabled="true"]': {
    pointerEvents: 'none',
    filter: 'grayscale(1)',
    opacity: 0.65,
  },
  height: '250px',
  [`${theme.breakpoints.down('sm')}`]: {
    height: '170px',
  },
}));

/**
 * Estimate the decoded byte length of a base64 data URL and check it's under
 * the upload limit. Applied to the *resized* payload (≤600px on the smaller
 * axis) — not the raw source — so users with high-res photos aren't blocked
 * for a size the actual upload would never hit. The identity-service has its
 * own server-side payload limit; this is a client-side sanity backstop.
 */
export const isValidImageSize = (base64Image: string | null) => {
  if (!base64Image) return undefined;
  const stringLength = base64Image.length;
  const sizeInMegaBytes = (Math.ceil(stringLength / 4) * 3) / 1024 / 1024;
  return sizeInMegaBytes <= maxPayloadMegabytes;
};

export const isValidImageExtension = (path?: string) => {
  if (!path) {
    return false;
  }
  const validExtensions = Object.values(AllowedImageFileExtensions);
  const extension: AllowedImageFileExtensions | 'unknown_file_extension' =
    (path?.split('.').pop()?.toLowerCase() as AllowedImageFileExtensions) ??
    'unknown_file_extension';

  return validExtensions.includes(extension);
};

export const UploadFormContent = ({
  uploading,
  selectedFile,
  fileValidation,
  setSelectedFile,
  setFileValidation,
}: UploadFormContentProps) => {
  const { clearErrors, setError, setValue, register } = useFormContext();

  const [, dispatch] = useContext(UploadImageModalContext);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      clearErrors();
      setFileValidation(initialValidationState);

      const file = acceptedFiles[0];
      const filePath = file?.name;

      if (!file || !isValidImageExtension(filePath)) {
        setFileValidation({ extension: invalidImageErrorMsg });
        setError('imageFile', { message: invalidImageErrorMsg });
        toast('error', invalidImageErrorMsg);
        return;
      }

      if (file.size > maxFileSizeBytes) {
        setFileValidation({ size: invalidFileSizeErrorMsg });
        setError('imageFile', { message: invalidFileSizeErrorMsg });
        toast('error', invalidFileSizeErrorMsg);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const sourceBase64 = reader.result as string;
        // Validate against the *resized* payload (matches what gets sent
        // to the avatar mutation by useUpdateUserAvatar) so we don't block
        // high-res sources that shrink well under the limit. Source
        // dimensions are NOT checked here because the resize/crop pipeline
        // preserves whatever native size the source has — see Cropping.tsx
        // and helpers/getCroppedImage.ts.
        let resized: string;
        try {
          resized = await resizeImage(sourceBase64);
        } catch {
          setFileValidation({ extension: invalidImageErrorMsg });
          setError('imageFile', { message: invalidImageErrorMsg });
          toast('error', invalidImageErrorMsg);
          return;
        }
        if (!isValidImageSize(resized)) {
          setFileValidation({ size: invalidFileSizeErrorMsg });
          setError('imageFile', { message: invalidFileSizeErrorMsg });
          toast('error', invalidFileSizeErrorMsg);
          return;
        }

        setSelectedFile(file);
        dispatch({ type: 'SET_BASE64_IMAGE', payload: sourceBase64 });
        dispatch({ type: 'ENABLE_CROP', payload: true });
        dispatch({ type: 'SET_BASE64_CROPPED_IMAGE', payload: null });
        setValue('imageFile', file.name, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      };

      reader.readAsDataURL(file);
    },
    [
      setError,
      setFileValidation,
      setSelectedFile,
      clearErrors,
      setValue,
      dispatch,
    ],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    disabled: uploading,
    maxFiles: 1,
    accept: getAcceptedDropzoneFiles(),
  });

  const handleRemoveImage = () => {
    dispatch({ type: 'REMOVE_IMAGE' });
    setSelectedFile(null);
    clearErrors();
    setFileValidation(initialValidationState);
    setValue('imageFile', null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const imagePreviewProps = {
    uploading,
    selectedFile,
    fileValidation,
    setFileValidation,
    handleRemoveImage,
  };

  return (
    <>
      <ImageBackground
        {...getRootProps()}
        sx={(theme) => ({
          px: {
            xs: theme.spacing(1),
            sm: theme.spacing(1),
            md: theme.spacing(4),
          },
          py: {
            xs: theme.spacing(1),
            sm: theme.spacing(1),
            md: theme.spacing(2),
          },
        })}
      >
        <input {...register('imageFile')} {...getInputProps()} id="dropzone" />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CloudUploadIcon
            color="primary"
            sx={{
              transition: 'color 200ms',
              fontSize: 32,
              mt: { xs: 1, md: 2 },
            }}
          />
          <Typography
            variant="h4"
            component="span"
            sx={{
              ml: 1,
              pb: 1,
            }}
          >
            Drag &amp; drop your files, or{' '}
            <Typography
              variant="h4"
              component="span"
              sx={{
                color: (t) => t.palette.primary.main,
                textDecoration: 'underline',
              }}
            >
              browse
            </Typography>
          </Typography>
          <Typography
            variant="body3"
            sx={{
              mt: 1,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            Supported file type:{' '}
            {Object.values(AllowedImageFileExtensions).join(', ')}.
          </Typography>
        </Box>
      </ImageBackground>
      <ImagePreview {...imagePreviewProps} />
    </>
  );
};
